// Zentrale Zendesk-Hilfen für das Frontend.
//
// Einzige Quelle für die Suchanfrage nach offenen Tickets eines Kunden im
// Frontend. Sowohl die Ticket-Zählung (über die Netlify-Routen) als auch die
// Agenten-Links im Fristenboard müssen dieselbe Menge treffen, sonst zeigt das
// Badge eine andere Zahl als die Trefferliste, die sich nach einem Klick öffnet.
//
// ACHTUNG: Die Suchbedingung steht ein zweites Mal in
// netlify/functions/_shared/zendeskAbfrage.js (genutzt von zendesk-tickets.js
// und zendesk-ticketzahlen.js). Die Netlify-Funktionen laufen in einer eigenen
// Laufzeitumgebung und können nicht aus src/ importieren. Eine Änderung hier
// muss dort nachgezogen werden.
//
// Ticketzahlen im Fristenboard: Die Badges lesen aus dem Zwischenspeicher
// zendesk_ticketzahlen (ladeGespeicherteTicketzahlen). Ist der Stand älter als
// eine Minute, stößt das Board über aktualisiereTicketzahlen einen neuen Abruf
// an. Die Prüfung vor der Finalisierung fragt dagegen weiterhin live bei
// Zendesk nach (ermittleOffeneTickets), weil es dort auf den genauen Stand
// ankommt.

import { netlifyFetch } from './netlifyFetch';
import { supabase } from './supabase';

/**
 * Tickets mit diesem Tag sind nicht abrechnungsrelevant und werden weder
 * gezählt noch in der verlinkten Zendesk-Trefferliste angezeigt.
 */
export const ZENDESK_NICHT_ABRECHNUNGSRELEVANT_TAG = 'nicht_abrechnungsrelevant';

/**
 * Ticketzahlen je Zendesk-Tag aus der Live-Abfrage. null bedeutet "unbekannt":
 * Die Abfrage für diesen Tag ist bei Zendesk gescheitert. Das ist bewusst nicht
 * dasselbe wie 0.
 */
export interface ZendeskTicketCount {
  [tag: string]: number | null;
}

/**
 * Gespeicherte Ticketzahl eines Zendesk-Tags aus zendesk_ticketzahlen.
 * anzahl ist null, solange für den Tag noch nie ein Abruf gelungen ist.
 * abgerufenAm ist der Zeitpunkt des letzten gelungenen Abrufs.
 */
export interface ZendeskTicketzahl {
  anzahl: number | null;
  abgerufenAm: string | null;
}

/**
 * Baut die Suchanfrage für offene, abrechnungsrelevante Tickets eines Kunden.
 * Das Minuszeichen ist in der Zendesk-Suchsyntax der Negations-Operator.
 * @param zendeskTag - Der Zendesk-Tag des Kunden
 */
export const buildZendeskOffeneTicketsQuery = (zendeskTag: string): string =>
  `tags:${zendeskTag} status<solved -tags:${ZENDESK_NICHT_ABRECHNUNGSRELEVANT_TAG}`;

/**
 * Baut den Link in die Zendesk-Agentensuche mit exakt derselben Anfrage, die
 * auch gezählt wird.
 * @param zendeskTag - Der Zendesk-Tag des Kunden
 */
export const buildZendeskAgentSuchUrl = (zendeskTag: string): string => {
  const subdomain = process.env.REACT_APP_ZENDESK_SUBDOMAIN;
  const query = buildZendeskOffeneTicketsQuery(zendeskTag);
  return `https://${subdomain}.zendesk.com/agent/search/1?type=ticket&q=${encodeURIComponent(query)}`;
};

export const fetchZendeskTickets = async (tags: string[]): Promise<ZendeskTicketCount> => {
  if (tags.length === 0) {
    return {};
  }

  try {
    const response = await netlifyFetch('/.netlify/functions/zendesk-tickets', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ tags })
    });

    if (!response.ok) {
      console.error('Zendesk API response not ok:', response.status);
      return {};
    }

    const data = await response.json();
    
    if (data.success) {
      return data.results;
    } else {
      console.error('Zendesk API error:', data.error);
      return {};
    }
  } catch (error) {
    console.error('Error fetching Zendesk tickets:', error);
    return {};
  }
};

/**
 * Prüft, ob ein Kunde offene Zendesk-Tickets hat
 * @param zendeskTag - Der Zendesk-Tag des Kunden
 * @returns Promise<boolean> - true wenn offene Tickets vorhanden, false wenn nicht
 */
export const hasOpenTickets = async (zendeskTag: string | null | undefined): Promise<boolean> => {
  if (!zendeskTag) {
    return false; // Kein Zendesk-Tag = keine Tickets möglich
  }

  try {
    const ticketCounts = await fetchZendeskTickets([zendeskTag]);
    const ticketCount = ticketCounts[zendeskTag] || 0;
    return ticketCount > 0;
  } catch (error) {
    console.error('Error checking open tickets:', error);
    return false; // Bei Fehler: Keine Blockierung
  }
};

/**
 * Holt die Anzahl offener Tickets für einen spezifischen Kunden
 * @param zendeskTag - Der Zendesk-Tag des Kunden
 * @returns Promise<number> - Anzahl offener Tickets
 */
export const getOpenTicketCount = async (zendeskTag: string | null | undefined): Promise<number> => {
  if (!zendeskTag) {
    return 0;
  }

  try {
    const ticketCounts = await fetchZendeskTickets([zendeskTag]);
    return ticketCounts[zendeskTag] || 0;
  } catch (error) {
    console.error('Error getting ticket count:', error);
    return 0;
  }
};

/**
 * Live-Prüfung der offenen Tickets eines Kunden, genutzt vor der
 * Finalisierung. Anders als getOpenTicketCount wird ein Fehler nicht als 0
 * gewertet: Scheitert die Abfrage, ist das Ergebnis null ("unbekannt"), und
 * der Aufrufer zeigt einen Hinweis statt stillschweigend fortzufahren.
 * Ohne Zendesk-Tag gibt es keine Tickets, das Ergebnis ist dann 0.
 * Wirft nie.
 * @param zendeskTag - Der Zendesk-Tag des Kunden
 */
export const ermittleOffeneTickets = async (zendeskTag: string | null | undefined): Promise<number | null> => {
  if (!zendeskTag) {
    return 0;
  }

  try {
    const ticketCounts = await fetchZendeskTickets([zendeskTag]);
    const anzahl = ticketCounts[zendeskTag];
    return typeof anzahl === 'number' ? anzahl : null;
  } catch (error) {
    console.error('Error checking open tickets:', error);
    return null;
  }
};

/**
 * Liest den Zwischenspeicher der Ticketzahlen (zendesk_ticketzahlen).
 * Liefert null, wenn die Tabelle nicht lesbar ist; der Aufrufer fällt dann
 * auf die Live-Abfrage zurück. Wirft nie.
 */
export const ladeGespeicherteTicketzahlen = async (): Promise<Map<string, ZendeskTicketzahl> | null> => {
  try {
    const { data, error } = await supabase
      .from('zendesk_ticketzahlen')
      .select('zendesk_tag, anzahl, abgerufen_am')
      .limit(100000);

    if (error) {
      console.error('Zendesk-Ticketzahlen nicht lesbar:', error.message);
      return null;
    }

    const zahlen = new Map<string, ZendeskTicketzahl>();
    (data || []).forEach((zeile: { zendesk_tag: string; anzahl: number | null; abgerufen_am: string | null }) => {
      zahlen.set(zeile.zendesk_tag, {
        anzahl: typeof zeile.anzahl === 'number' ? zeile.anzahl : null,
        abgerufenAm: zeile.abgerufen_am ?? null
      });
    });
    return zahlen;
  } catch (error) {
    console.error('Zendesk-Ticketzahlen nicht lesbar:', error);
    return null;
  }
};

/**
 * Stößt den Abruf der Ticketzahlen bei Zendesk an (Netlify-Funktion
 * zendesk-ticketzahlen). Die Funktion ruft höchstens einmal pro Minute bei
 * Zendesk ab, auch wenn mehrere Boards gleichzeitig anfragen.
 * Liefert true, wenn dieser Aufruf tatsächlich abgerufen hat. Wirft nie.
 */
export const aktualisiereTicketzahlen = async (): Promise<boolean> => {
  try {
    const response = await netlifyFetch('/.netlify/functions/zendesk-ticketzahlen', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: '{}'
    });

    if (!response.ok) {
      console.error('Zendesk-Ticketzahlen: Abruf fehlgeschlagen:', response.status);
      return false;
    }

    const data = await response.json();
    return data?.success === true && data?.aktualisiert === true;
  } catch (error) {
    console.error('Zendesk-Ticketzahlen: Abruf fehlgeschlagen:', error);
    return false;
  }
};
